<script setup lang="ts">
import {
  UIButton,
  UIIcon,
  UIInputToggleRadio,
  UITag,
} from '@groover-dev/groover-ui'

import { vConvertToTargetCurrency } from '~/directives/ConvertToTargetCurrency'

import CurrencyOrder from '~/components/shared/currency/order.vue'
import CurrencySwapper from '~/components/shared/currency/swapper.vue'

import { useCurrencySetter } from '~/composables/useCurrencySetter'

import { useMiscCurrencyStore } from '~/stores/miscCurrency'

import { provideFetchPlanCheckout } from '~/api-core/Subscription/Plan'

import { PlanIntervals } from '~/enums/Product'

import type { Plan } from '~/types/Subscription'

type Props = {
  theme?: 'light' | 'dark'
  learnMoreUrl?: string | null
  learnMoreText?: string | null
  planId?: number
  ctaVariant?: 'primary' | 'outlined' | 'transparent'
  initSelectedPlanId?: boolean
}

const {
  theme = 'dark',
  learnMoreUrl = null,
  learnMoreText = null,
  planId,
  ctaVariant = 'primary',
  initSelectedPlanId = false,
} = defineProps<Props>()

const { t } = useI18n()
const { coreFetch } = useProvideCoreFetch()
const stripeCheckoutModalClientSecret = useState<string | undefined>(
  'stripeCheckoutModalClientSecret',
  () => undefined,
)
const { initCurrency } = useCurrencySetter()

const { targetCurrency: TARGET_CURRENCY } = storeToRefs(useMiscCurrencyStore())
const { plans, selectedPlanId } = storeToRefs(useSubscriptionStore())
const { FETCH_PLANS, SET_SELECTED_PLAN_ID, INIT_SELECTED_PLAN_ID } =
  useSubscriptionStore()

await Promise.all([
  callOnce('FETCH_PLANS', FETCH_PLANS),
  callOnce('INITIALIZE_CURRENCY_STORE', initCurrency),
])

if (initSelectedPlanId) INIT_SELECTED_PLAN_ID()
else SET_SELECTED_PLAN_ID(0)

onBeforeUnmount(() => {
  if (initSelectedPlanId) SET_SELECTED_PLAN_ID(0)
})

function getPlan(planId: number) {
  return plans.value.find((p: Plan) => p.id === planId)
}

const plan = computed(() => getPlan(planId ?? selectedPlanId.value))

type ToggleRadioOption<T> = { label: string; value: T }

const options = computed<ToggleRadioOption<number>[]>(() =>
  plans.value.map((p: Plan) => ({
    label: t(`subscription.interval.${p.interval}`),
    value: p.id,
  })),
)

const textColorClass = computed(() =>
  theme === 'dark' ? 'tw-text-inverse' : 'tw-text-color-default',
)
const bgColorClass = computed(() =>
  theme === 'dark' ? 'tw-bg-[#131313]' : 'tw-bg-fill',
)
const benefitsBgClass = computed(() =>
  theme === 'dark' ? 'tw-bg-fill-alt' : 'tw-bg-surface',
)

const shouldUseCurrencyConversion = computed(() => {
  return !Object.keys(plan.value?.currency_options ?? {}).includes(
    TARGET_CURRENCY.value,
  )
})

function getDisplayPrice(plan: Plan) {
  return (
    plan.currency_options[TARGET_CURRENCY.value] ??
    plan.currency_options[plan.currency]
  )
}

function calculateMonthlyPrice(plan: Plan) {
  const INTERVAL_MULTIPLIERS = {
    [PlanIntervals.YEAR]: 1 / 12,
    [PlanIntervals.MONTH]: 1,
  } as const

  return Number(
    (
      getDisplayPrice(plan) * (INTERVAL_MULTIPLIERS[plan.interval] ?? 1)
    ).toFixed(2),
  )
}
async function subscribeSelectedPlan() {
  const fetchPlanCheckout = provideFetchPlanCheckout(coreFetch)

  const { client_secret: clientSecret } = await fetchPlanCheckout({
    planId: selectedPlanId.value,
    returnUrl: location.href,
    currency: TARGET_CURRENCY.value,
  })

  stripeCheckoutModalClientSecret.value = clientSecret
}

function handleCTAClick() {
  if (planId) SET_SELECTED_PLAN_ID(planId)

  subscribeSelectedPlan()
}
</script>

<template>
  <div
    v-if="plans.length > 0 && plan"
    class="tw-flex tw-flex-col tw-rounded-md tw-p-xs"
    :class="bgColorClass"
  >
    <div class="tw-flex tw-flex-col tw-gap-xl tw-p-xl">
      <div>
        <div class="tw-flex tw-items-start tw-justify-between tw-gap-xl">
          <span class="tw-text-h3" :class="textColorClass">
            {{ t(`subscription.plan.groover-plus-${plan.interval}ly`) }}
          </span>

          <UITag
            v-if="plan.interval === PlanIntervals.YEAR"
            :text="t('subscription.pricingCard.savePercent')"
            color="accent"
            class="tw-whitespace-nowrap"
            size="sm"
          />
        </div>
        <div
          class="tw-text-h5 tw-flex tw-items-baseline tw-gap-2xs"
          :class="textColorClass"
        >
          <span>
            <span v-if="shouldUseCurrencyConversion">≈&nbsp;</span
            ><CurrencyOrder class="tw-inline-block">
              <span
                v-if="shouldUseCurrencyConversion"
                v-convert-to-target-currency.noRounding="
                  calculateMonthlyPrice(plan)
                "
              />
              <span v-else>{{
                calculateMonthlyPrice(plan)
              }}</span></CurrencyOrder
            >/{{ t(`common.month`) }}
          </span>
          <CurrencySwapper class="tw-leading-6" color="accent" :theme="theme" />
        </div>
        <span class="tw-text-body-sm tw-text-discrete3">
          <i18n-t
            v-if="plan.interval === PlanIntervals.YEAR"
            keypath="subscription.pricingCard.billedAnnually"
          >
            <template #price
              ><span v-if="shouldUseCurrencyConversion" class="tw-mr-3xs"
                >≈</span
              ><CurrencyOrder class="tw-inline-block"
                ><span
                  v-if="shouldUseCurrencyConversion"
                  v-convert-to-target-currency.noRounding="
                    getDisplayPrice(plan)
                  "
                />
                <span v-else>{{ getDisplayPrice(plan) }}</span></CurrencyOrder
              ></template
            >
          </i18n-t>
          <!-- To keep the layout consistent -->
          <span v-else class="tw-text-body-sm">&nbsp;</span>
        </span>
      </div>
      <UIInputToggleRadio
        v-if="!planId"
        data-test-id="subscription-plan-toggle"
        class="tw-self-center"
        :model-value="selectedPlanId"
        :options="options"
        :theme="theme"
        @update:model-value="SET_SELECTED_PLAN_ID($event as number)"
      />
    </div>
    <div class="tw-flex tw-flex-col tw-gap-xl tw-p-xl" :class="benefitsBgClass">
      <ul class="tw-flex tw-flex-col tw-gap-md">
        <li
          v-for="[benefit, enabled] in Object.entries(plan.benefits)"
          :key="benefit"
          class="tw-inline-flex tw-items-center tw-gap-xs"
          :class="enabled ? textColorClass : 'tw-text-discrete2'"
        >
          <UIIcon
            size="sm"
            :name="enabled ? 'mdi:check' : 'mdi:close'"
            :class="
              enabled
                ? theme === 'light'
                  ? 'tw-text-accent'
                  : 'tw-text-inverse-accent'
                : 'tw-text-discrete2'
            "
          />
          {{ t(`subscription.pricingCard.benefits.${benefit}`) }}
        </li>
      </ul>
      <UIButton
        :text="t(`subscription.pricingCard.CTAStartWith.${plan.interval}`)"
        :color="
          theme === 'dark' && ctaVariant === 'outlined'
            ? 'alt-accent'
            : 'accent'
        "
        :variant="ctaVariant"
        @click="handleCTAClick"
      />
      <UIButton
        v-if="learnMoreText && learnMoreUrl"
        :text="learnMoreText"
        :color="theme === 'dark' ? 'alt-accent' : 'accent'"
        variant="outlined"
        :to="learnMoreUrl"
      />
    </div>
  </div>
</template>

<style scoped>
:deep(label.tw-bg-fill-alt) {
  background-color: #131313;
}
</style>
